import { useRoute, useRouter } from 'vue-router';

import { onMounted, shallowRef, useId, useState, watch } from '#imports';
import URLParamsHelper from '@/utils/setQueryParams';

type WatchParamsType = Record<string, unknown> & Partial<AppPagination>;

export default function useWatchQueryParams<T extends WatchParamsType>(initialParams: T, name: string) {
    const route = useRoute();
    const router = useRouter();

    const id = useId();

    const routeParams = new URLParamsHelper<T>(
        initialParams
            ? initialParams
            : {} as T,
        route.fullPath
    );

    const query = shallowRef<T>(routeParams.getParams());
    const totalPgn = useState<number>(
        id,
        () => 0
    );
    const prevRouteName = route.name;
    // const historyStatePosition = ref<Nullable<number>>(null);

    const onChangeQueryParams = async (newParams: Partial<T>) => {
        query.value = {
            ...query.value,
            ...newParams
        };
        const newPath = new URLParamsHelper(
            query.value,
            route.fullPath
        ).buildParams();
        await router.push(newPath);
    };

    const setTotal = (val: number) => {
        totalPgn.value = val;
    };

    const guardQueryParams = async (params: {
        page?: number;
        size?: number;
        total?: number;
    }) => {
        if (params?.total !== totalPgn.value && totalPgn.value !== 0) {
            if (params.size && params?.total && params.size >= params.total) {
                query.value = {
                    ...query.value,
                    page: 1,
                    size: params.total
                };
            }

            // todo https://xsolla.atlassian.net/browse/NFTM-402 пересчет страниц, можно чекнуть ноушен с видео
            else if (
                params.size && params?.total && initialParams?.size
                && params.size <= params.total
                && params.total < initialParams.size
            ) {
                query.value = {
                    ...query.value,
                    page: 1,
                    size: params.total
                };
            }
            else if (
                params.size
                && params?.total
                && initialParams?.size
                && params.size <= params.total
                && params.total > initialParams.size
            ) {
                query.value = {
                    ...query.value,
                    size: initialParams.size
                };
            }
            else if (!params.size && params.total && initialParams?.size) {
                query.value.page = Math.ceil(totalPgn.value / initialParams.size);
            }

            await router.replace({
                query: {
                    ...route.query,
                    ...route.query?.page && route.query.page !== query.value.page
                        ? { page: query.value.page }
                        : {},
                    ...route.query?.size && route.query.size !== query.value.size
                        ? { size: query.value.size }
                        : {}
                }
            });
        }

        if (params.total !== undefined) totalPgn.value = params.total;
    };

    function getMainRoute(path: string | null) {
        if (path === null) return null;
        const match = path.match(/^\/([^/?#]+\/?[^/?#]*)/);
        return match
            ? match[0]
            : null;
    }

    function hasUrlParams(url: string) {
        return (/\?.+=.+/).test(url);
    }

    const handlePopState = (params: PopStateEvent) => {
        const routeParams = new URLParamsHelper(query.value, params.state.current).getParams();

        if (hasUrlParams(params.state.current)) {
            query.value = { ...query.value, ...routeParams };
        }
    };

    const subscribe = useState(() => false);

    const addListener = () => {
        if (!subscribe.value) {
            window.addEventListener(
                'popstate',
                handlePopState
            );
            subscribe.value = true;
        }
    };

    const removeListener = () => {
        window.removeEventListener(
            'popstate',
            handlePopState
        );
        subscribe.value = false;
    };

    onMounted(() => {
        addListener();
    });

    onUnmounted(() => {
        removeListener();
    });

    router.beforeEach((to, from, next) => {
        if (name !== getMainRoute(to.path) && name === getMainRoute(from.path)) {
            removeListener();
        }
        next();
    });

    router.afterEach((to, from) => {
        if (name === getMainRoute(to.path) && name !== getMainRoute(from.path)) {
            addListener();
        }
    });

    watch(
        [
            () => route.query,
            router.currentRoute
        ],
        ([
            queryParams,
            routerCurrentRoute
        ]) => {
            if (prevRouteName !== routerCurrentRoute.name) return;
            if (
                !Object.keys(queryParams).length
            ) {
                query.value = initialParams;
            }
        }
    );

    return {
        onChangeQueryParams,
        query,
        guardQueryParams,
        totalPgn,
        setTotal
    } as const;
}
